import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Spinner, Alert } from 'react-bootstrap';
import { enablePreview } from '../../lib/preview';

function normalizeType(t?: string | null) {
  const v = String(t || '').toLowerCase();
  if (!v) return '';
  if (v === 'home' || v === 'homepage' || v === 'api::homepage.homepage') return 'homepage';
  if (v === 'faq' || v === 'faq-page' || v.includes('faq-page')) return 'faq-page';
  if (v.includes('videos-takeover-page')) return 'videos-takeover-page';
  if (v === 'current-year' || v === 'current-year-page' || v.includes('current-year-page')) return 'current-year-page';
  if (v === 'previous-year' || v.includes('previous-year')) return 'previous-year';
  if (v === 'who-we-are' || v.includes('who-we-are')) return 'who-we-are';
  if (v.includes('submission-confirmation-page')) return 'submission-confirmation-page';
  if (v === 'submission' || v.includes('submission')) return 'submission';
  if (v === 'page' || v.includes('page')) return 'page';
  return v;
}

function buildTargetPath(type: string, params: URLSearchParams): string | null {
  const slug = params.get('slug') || params.get('Slug') || '';
  switch (type) {
    case 'homepage':
      return '/';
    case 'faq-page':
      return '/faq';
    case 'videos-takeover-page':
      return '/videos';
    case 'current-year-page':
      // Our app uses flattened campaign routes; default to schedule
      return '/schedule';
    case 'previous-year':
      // App route is /recap
      return '/recap';
    case 'who-we-are':
      return '/who-we-are';
    case 'submission-confirmation-page':
      // Show confirmation view on submit page in preview mode
      return '/nominate-a-charity?preview=success';
    case 'submission':
      return slug ? `/videos/${slug}` : '/videos';
    case 'page':
      return slug ? `/${slug}` : '/';
    default:
      // Fallback to home
      return '/';
  }
}

export default function Preview() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const type = useMemo(() => normalizeType(searchParams.get('type') || searchParams.get('contentType')), [searchParams]);
  const secret = searchParams.get('secret') || '';

  useEffect(() => {
    const expected = process.env.REACT_APP_STRAPI_PREVIEW_SECRET || process.env.STRAPI_PREVIEW_SECRET || '';
    if (!secret || !expected || secret !== expected) {
      setError('Invalid preview secret.');
      return;
    }

    // Enable preview mode and patch fetch
    console.log('Enabling preview mode'); enablePreview();
    // Persist target in session for scoped draft fetching
    try {
      const payload = { type, slug: searchParams.get('slug') || searchParams.get('Slug') || null };
      sessionStorage.setItem('p4a_preview_target', JSON.stringify(payload));
    } catch {}

    const path = buildTargetPath(type, searchParams) || '/';
    // Persist the path for scoping draft fetches to this page only
    try { sessionStorage.setItem('p4a_preview_path', path); } catch {}

    // Append preview=1 to help components detect preview without relying on session
    const url = new URL(window.location.origin + path);
    url.searchParams.set('preview', '1');

    // Small delay to ensure fetch patch is active
    const t = setTimeout(() => {
      navigate(url.pathname + url.search, { replace: true });
    }, 10);
    return () => clearTimeout(t);
  }, [type, secret, navigate, searchParams]);

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4 text-center">
      <Spinner animation="border" />
    </Container>
  );
}
